import React, { useState, useRef, useEffect } from "react";
import AltCategoriesTable from "../tables/AltCategoriesTable";
import NewAltCategoryModal from "./NewAltCategoryModal";
import { successMsg } from "../SuccessHandler";
import ErrorCodes from "../../constants/ErrorCodes";
import {
  useUpdateFunctions,
  useInsertFunctions,
  useSelectFunctions,
} from "../../functions";
import ConfirmDialog from "../utilities/ConfirmDialog";

const AltCategoryModal = ({
  data,
  trackCategoryCbx,
  altCategories,
  setAltCategories,
  getAltCategory,
  profiles,
  gameProviders,
  shouldFocusLast,
  setShouldFocusLast,
  setSessionNoLongerExists,
  openNewAltCategoryModal,
  setOpenNewAltCategoryModal,
  getTracksListCbx,
  tracksListCbx,
}) => {
  const closeButtonRef = useRef();
  const [hasChanges, setHasChanges] = useState(false);
  const tableRef = useRef(null);
  const divRef = useRef(null);
  const [newAltCategory, setNewAltCategory] = useState({});
  const [closeModalState, setCloseModalState] = useState(false);
  const [associatedProfiles, setAssociatedProfiles] = useState([]);
  const [viewProfiles, setViewProfiles] = useState(false);
  const [tracksByProvider, setTracksByProvider] = useState([]);
  const { updateTrackAltCategory } = useUpdateFunctions();
  const { insertTrackAltCategory } = useInsertFunctions();
  const { getTrackListEnabled } = useSelectFunctions();

  useEffect(() => {
    if (openNewAltCategoryModal) {
      getTracksListCbx(-1);
    }
  }, [openNewAltCategoryModal]);

  // Update Alt Categories Function
  const updateAltCategories = (altCategories, close) => {
    const rowsWithChanges = altCategories.filter(r => r.Change);
  
    if (rowsWithChanges.length === 0) {
      if (close) {
        closeButtonRef.current.click();
        setAltCategories([]);
        setAssociatedProfiles([]);
        setViewProfiles(false);
      }
      return;
    }
    
    if (hasChanges) {
      let hasError = false;

      altCategories.forEach((row) => {
        [row].forEach((cell) => {
          if (!cell.DateFrom.trim()) {
            hasError = true;
          }
        });
      });

      if (hasError && close) {
        closeButtonRef.current.click();
        setAltCategories([]);
      } else if (!hasError) {
        let msgCancel;
        if (close) {
          msgCancel = "Exit anyway";
        } else {
          msgCancel = "Cancel";
        }
        ConfirmDialog(
          `Do you want to save the changes?`,
          msgCancel,
          "Yes, save it",
        ).then(async (result) => {
          if (result.isConfirmed) {
            let InParams = [];
            altCategories.forEach((element) => {
              if (element.Change) {
                InParams.push({
                  ...element,
                });
              }
            });

            const res = await updateTrackAltCategory(
              {
                TrackAltCategoryList: InParams,
              },
              data.LogonSessionID,
            );

            if (res?.error) {
              if (
                res.Result === ErrorCodes.InvalidSession ||
                res.Result === ErrorCodes.SessionNoLongerExists
              ) {
                setSessionNoLongerExists(true);
              }
              return;
            } else {
              successMsg("Save Alt Categories");
              setHasChanges(false);
              if (close) {
                closeButtonRef.current.click();
                setAltCategories([]);
                setAssociatedProfiles([]);
                setHasChanges(false);
                setViewProfiles(false);
              }
            }
          } else {
            if (close) {
              closeButtonRef.current.click();
              setAltCategories([]);
              setAssociatedProfiles([]);
              setHasChanges(false);
              setViewProfiles(false);
            }
          }
        });
      }
    }
  };

  //Insert Alt Category Function
  const insertAltCategory = async (newAltCategory) => {
    const _errors = {};
    if (!newAltCategory.DateFrom) _errors.dateFrom = "Date is required";

    if (Object.keys(_errors).length === 0) {
      const res = await insertTrackAltCategory(
        {
          ...newAltCategory,
        },
        data.LogonSessionID,
      );

      if (res?.error) {
        if (
          res.Result === ErrorCodes.InvalidSession ||
          res.Result === ErrorCodes.SessionNoLongerExists
        ) {
          setSessionNoLongerExists(true);
        }
        return;
      } else {
        if (res.Result === 0) {
          successMsg("Save Alt Category");
          getAltCategory(true);
        }

        setCloseModalState(!closeModalState);
      }

      setCloseModalState(!closeModalState);
      setShouldFocusLast(false);
    }
  };

  // This function is executed when closing the modal from the close button or from x
  const closeModal = () => {
    if (hasChanges) {
      updateAltCategories(altCategories, true);
    } else {
      closeButtonRef.current.click();
      const div = divRef.current;
      if (div) {
        div.scrollTop = 0;
      }

      setOpenNewAltCategoryModal(false);

      const rows = tableRef.current.getElementsByTagName("tr");
      const lastRow = rows[rows.length - 1];
      lastRow.style.backgroundColor = null;
      setAltCategories([]);
      setAssociatedProfiles([]);
      setViewProfiles(false);
    }
  };

  const getTracksListCbxNew = async (providerID) => {
    const res = await getTrackListEnabled(
      {
        ProviderID: providerID,
      },
      data.LogonSessionID,
    );

    if (res?.error) {
      if (
        res.Result === ErrorCodes.InvalidSession ||
        res.Result === ErrorCodes.SessionNoLongerExists
      ) {
        setSessionNoLongerExists(true);
      }
      return;
    } else {
      setTracksByProvider(res.TrackList);
      return res.TrackList;
    }
  };

  useEffect(() => {
    const hasRealChanges = altCategories?.some(row => row.Change);
    setHasChanges(hasRealChanges);
  }, [altCategories]);
  

  return (
    <>
      <div
        className="modal fade"
        id="altCategoryModal"
        role="dialog"
        aria-hidden="false"
        data-keyboard="false"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header headerModals">
              <h5 className="modal-title" id="altCategoryModal">
                Alternate Category
              </h5>
              <span
                data-dismiss="modal"
                ref={closeButtonRef}
                style={{ cursor: "inherit" }}
              ></span>
              <button type="button" className="close" onClick={closeModal}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              {altCategories ? (
                <AltCategoriesTable
                  altCategories={altCategories}
                  setAltCategories={setAltCategories}
                  data={data}
                  divRef={divRef}
                  tableRef={tableRef}
                  shouldFocusLast={shouldFocusLast}
                  setShouldFocusLast={setShouldFocusLast}
                  setHasChanges={setHasChanges}
                  trackCategoryCbx={trackCategoryCbx}
                  trackList={tracksListCbx}
                  profiles={profiles}
                  associatedProfiles={associatedProfiles}
                  setAssociatedProfiles={setAssociatedProfiles}
                  viewProfiles={viewProfiles}
                  setViewProfiles={setViewProfiles}
                  setSessionNoLongerExists={setSessionNoLongerExists}
                />
              ) : (
                <div style={{ textAlign: "center", color: "#808080" }}>
                  <span className="parpadea">Loading...</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <div className="col-sm-6">
                <button
                  type="button"
                  className="btn btn-info"
                  onClick={() => getAltCategory(false)}
                >
                  Refresh
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  data-toggle="modal"
                  data-target="#newAltCategoryModal"
                >
                  New Alt Category
                </button>
              </div>
              <div
                className="col-sm-6"
                style={{ textAlign: "right", paddingRight: "0px" }}
              >
                <button
                  type="button"
                  className="btn btn-success"
                  disabled={!hasChanges}
                  onClick={() => updateAltCategories(altCategories, false)}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <NewAltCategoryModal
        trackList={tracksByProvider}
        trackCategoryCbx={trackCategoryCbx}
        profiles={profiles}
        newAltCategory={newAltCategory}
        setNewAltCategory={setNewAltCategory}
        closeModalState={closeModalState}
        setCloseModalState={setCloseModalState}
        insertAltCategory={insertAltCategory}
        gameProviders={gameProviders}
        altCategories={altCategories}
        getTracksListCbxNew={getTracksListCbxNew}
      />
    </>
  );
};

export default AltCategoryModal;
